<script setup>
// S16 — Map Shopify Locations to existing Ravo Sites.
// Per-location: Link existing Site | Create Site | Ignore. Name/address are
// SUGGESTIONS only — the operator must explicitly confirm each action. This is
// the SOLE editable Location->Site mapping surface (Edit-Site is read-only).
import { ref, computed, onBeforeMount, watch } from "vue";
import { useCompanyStore } from "@/stores/company";
import { useShopifyStore } from "@/stores/shopify";
import { useSitesStore } from "@/stores/sites";
import {
  MAPPING_STATUSES,
  IGNORE_REASONS,
  isProviderGid,
  formatProviderAddress,
} from "@/utils/helpers/shopify";

const props = defineProps({
  integrationId: { type: String, required: true },
});
const emit = defineEmits(["mapped", "conflict"]);

const shopifyStore = useShopifyStore();
const sitesStore = useSitesStore();
const company_id = useCompanyStore().companyId;

const siteOptions = ref([]);
const loadingSites = ref(true);
const savingId = ref("");
const conflictMessage = ref("");
const locationSearch = ref("");
const mappingFilter = ref("all");

// Per-location editing draft keyed by internal provider_locations.id.
const draft = ref({});

const locations = computed(() => shopifyStore.locations);
const pending = computed(() => shopifyStore.pendingActiveLocations());
const allResolved = computed(() => pending.value.length === 0);
const visibleLocations = computed(() => {
  const search = locationSearch.value.trim().toLocaleLowerCase();

  return locations.value.filter((location) => {
    const status = location.mapping?.status || MAPPING_STATUSES.PENDING;
    const matchesStatus = mappingFilter.value === "all" || status === mappingFilter.value;
    const matchesSearch = !search || [location.display_name, formatProviderAddress(location.address)]
      .filter(Boolean)
      .join(" ")
      .toLocaleLowerCase()
      .includes(search);

    return matchesStatus && matchesSearch;
  });
});

onBeforeMount(async () => {
  await loadSites();
});

// The parent reads locations asynchronously. Rebuild drafts from that durable data
// whenever the list is replaced so a first discovery cannot leave rows without an
// editable action state.
watch(locations, seedDrafts, { immediate: true });

async function loadSites() {
  loadingSites.value = true;
  try {
    const res = await sitesStore.fetchSites(company_id);
    // Only company-owned sites; a provider GID can never appear as a site id.
    siteOptions.value = (res?.data || []).filter((s) => !isProviderGid(s.id));
  } catch (e) {
    siteOptions.value = [];
  } finally {
    loadingSites.value = false;
  }
}

function seedDrafts() {
  const next = {};
  for (const loc of locations.value) {
    const status = loc.mapping?.status || MAPPING_STATUSES.PENDING;
    next[loc.id] = {
      action:
        status === MAPPING_STATUSES.MAPPED
          ? "link"
          : status === MAPPING_STATUSES.IGNORED
          ? "ignore"
          : null,
      ravo_site_id: loc.mapping?.ravo_site_id || null,
      // The provider address only pre-fills the normal Ravo Site location field
      // when the operator explicitly chooses Create Site. It never identifies a
      // Site and is never used for automatic matching. loc.address is a
      // structured object (Shopify's own Location.address shape) — format it
      // into a readable string rather than assigning it raw.
      newSiteLocation: formatProviderAddress(loc.address),
      ignore_reason: loc.mapping?.ignore_reason || null,
      version: loc.mapping?.version ?? 0,
    };
  }
  draft.value = next;
}

function setAction(locId, action) {
  const d = draft.value[locId];
  d.action = action;
  if (action !== "link" && action !== "create") d.ravo_site_id = null;
  if (action !== "ignore") d.ignore_reason = null;
}

function siteLabel(siteId) {
  const site = siteOptions.value.find((candidate) => candidate.id === siteId);
  if (!site) return siteId;

  const areas = Array.isArray(site.areas) ? site.areas : [];
  const deviceCount = areas.reduce(
    (count, area) => count + (Array.isArray(area.devices) ? area.devices.length : 0),
    0
  );
  const context = [
    site.location,
    deviceCount ? `${deviceCount} device${deviceCount === 1 ? "" : "s"}` : "no devices",
  ].filter(Boolean);

  return context.length ? `${site.name} - ${context.join(" - ")}` : site.name;
}

function isMappedElsewhere(siteId, currentLocationId) {
  return locations.value.some(
    (location) =>
      location.id !== currentLocationId &&
      location.mapping?.status === MAPPING_STATUSES.MAPPED &&
      location.mapping?.ravo_site_id === siteId
  );
}

function availableSiteOptions(currentLocationId) {
  return siteOptions.value
    .filter((site) => !isMappedElsewhere(site.id, currentLocationId))
    .map((site) => site.id);
}

function canConfirm(locId) {
  const d = draft.value[locId];
  if (!d || !d.action) return false;
  if (d.action === "link") return !!d.ravo_site_id;
  if (d.action === "create") return !!(d.newSiteName && d.newSiteName.trim());
  if (d.action === "ignore") return IGNORE_REASONS.includes(d.ignore_reason);
  return false;
}

async function confirm(loc) {
  const d = draft.value[loc.id];
  if (!canConfirm(loc.id)) return;
  conflictMessage.value = "";
  savingId.value = loc.id;

  const change = {
    provider_location_id: loc.id, // INTERNAL uuid row id (not the opaque GID)
    action: d.action,
    version: d.version,
  };
  if (d.action === "link") change.ravo_site_id = d.ravo_site_id;
  if (d.action === "create") {
    // `site_name` is the frozen ai-back request field. The provider address remains
    // a user-editable Site profile field, never an identity or catalogue scope.
    change.site_name = d.newSiteName.trim();
    if (d.newSiteLocation.trim()) change.site_location = d.newSiteLocation.trim();
  }
  if (d.action === "ignore") change.ignore_reason = d.ignore_reason;

  try {
    await shopifyStore.saveMappings(company_id, props.integrationId, [change]);
    seedDrafts();
    emit("mapped");
  } catch (err) {
    if (err?.status === 409) {
      conflictMessage.value =
        err?.data?.message ||
        "This location was changed by someone else. It has been refreshed — review and retry.";
      seedDrafts();
      emit("conflict", err);
    }
  } finally {
    savingId.value = "";
  }
}

defineExpose({
  draft,
  setAction,
  confirm,
  canConfirm,
  allResolved,
  siteOptions,
  visibleLocations,
  siteLabel,
  availableSiteOptions,
});
</script>

<template>
  <div class="map-step">
    <div v-if="conflictMessage" class="shopify-alert" role="alert" data-test="map-conflict">
      {{ conflictMessage }}
    </div>

    <div class="map-step__head">
      <h3>Map locations</h3>
      <span v-if="allResolved" class="map-step__ok" data-test="all-resolved">
        <i class="bi bi-check-circle"></i> All active locations resolved
      </span>
      <span v-else class="map-step__pending" data-test="pending-count">
        {{ pending.length }} location(s) still need a decision
      </span>
    </div>

    <div class="map-step__filters" aria-label="Filter provider locations">
      <input
        v-model="locationSearch"
        type="search"
        class="map-search"
        placeholder="Search locations"
        data-test="location-search"
      />
      <select v-model="mappingFilter" class="map-filter" data-test="mapping-filter">
        <option value="all">All locations</option>
        <option :value="MAPPING_STATUSES.PENDING">Needs decision</option>
        <option :value="MAPPING_STATUSES.MAPPED">Mapped</option>
        <option :value="MAPPING_STATUSES.IGNORED">Ignored</option>
      </select>
    </div>

    <div v-if="loadingSites" class="shopify-center" data-test="map-loading">
      <span class="spinner-sm" role="status" aria-hidden="true"></span>
      <p class="mt-1">Loading Sites…</p>
    </div>

    <table v-else class="map-table" data-test="map-table">
      <thead>
        <tr>
          <th>Shopify location</th>
          <th>Suggestion</th>
          <th>Action</th>
          <th>Destination</th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="loc in visibleLocations" :key="loc.id" :data-test="`loc-row-${loc.id}`">
          <td>
            <b>{{ loc.display_name }}</b>
            <span v-if="loc.provider_active === false" class="map-badge map-badge--warn">
              inactive
            </span>
          </td>
          <td class="map-table__suggest">
            <!-- suggestion only; never establishes identity -->
            {{ formatProviderAddress(loc.address) || "—" }}
          </td>
          <td>
            <div class="map-actions">
              <button
                type="button"
                class="map-chip"
                :class="{ 'map-chip--on': draft[loc.id] && draft[loc.id].action === 'link' }"
                @click="setAction(loc.id, 'link')"
                :data-test="`action-link-${loc.id}`"
              >
                Link existing
              </button>
              <button
                type="button"
                class="map-chip"
                :class="{ 'map-chip--on': draft[loc.id] && draft[loc.id].action === 'create' }"
                @click="setAction(loc.id, 'create')"
                :data-test="`action-create-${loc.id}`"
              >
                Create Site
              </button>
              <button
                type="button"
                class="map-chip"
                :class="{ 'map-chip--on': draft[loc.id] && draft[loc.id].action === 'ignore' }"
                @click="setAction(loc.id, 'ignore')"
                :data-test="`action-ignore-${loc.id}`"
              >
                Ignore
              </button>
            </div>
          </td>
          <td>
            <VMultiselect
              v-if="draft[loc.id] && draft[loc.id].action === 'link'"
              v-model="draft[loc.id].ravo_site_id"
              :options="availableSiteOptions(loc.id)"
              :custom-label="siteLabel"
              :searchable="true"
              :close-on-select="true"
              :show-labels="false"
              placeholder="Choose Ravo Site"
              :data-test="`site-select-${loc.id}`"
            />
            <DInput
              v-else-if="draft[loc.id] && draft[loc.id].action === 'create'"
              :value="draft[loc.id].newSiteName || ''"
              @input="draft[loc.id].newSiteName = $event.target.value"
              label="New Site name"
              width="100%"
            />
            <DInput
              v-if="draft[loc.id] && draft[loc.id].action === 'create'"
              :value="draft[loc.id].newSiteLocation || ''"
              @input="draft[loc.id].newSiteLocation = $event.target.value"
              label="Site location"
              width="100%"
            />
            <select
              v-else-if="draft[loc.id] && draft[loc.id].action === 'ignore'"
              v-model="draft[loc.id].ignore_reason"
              class="map-select"
              :data-test="`ignore-reason-${loc.id}`"
            >
              <option :value="null" disabled>Reason…</option>
              <option v-for="r in IGNORE_REASONS" :key="r" :value="r">{{ r }}</option>
            </select>
            <span v-else class="map-table__muted">Choose an action</span>
          </td>
          <td>
            <button
              type="button"
              class="btn btn-primary btn-sm"
              :disabled="!canConfirm(loc.id) || savingId === loc.id"
              @click="confirm(loc)"
              :data-test="`confirm-${loc.id}`"
            >
              Confirm
              <span v-if="savingId === loc.id" class="spinner-sm" role="status" aria-hidden="true"></span>
            </button>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<style lang="scss" scoped>
.shopify-alert {
  background: var(--red-50, #fef2f2);
  color: var(--red-700, #b91c1c);
  border: 1px solid var(--red-200, #fecaca);
  border-radius: 8px;
  padding: 8px 12px;
  margin-bottom: 12px;
  font-size: var(--text-sm);
}
.shopify-center { text-align: center; padding: 16px 0; }
.map-step__head { display: flex; align-items: center; gap: 12px; margin-bottom: 10px; }
.map-step__ok { color: var(--green-600, #16a34a); font-size: var(--text-sm); }
.map-step__pending { color: var(--amber-600, #d97706); font-size: var(--text-sm); }
.map-step__filters { display: flex; gap: 8px; margin-bottom: 10px; }
.map-search, .map-filter { border: 1px solid var(--neutral-300); border-radius: 6px; padding: 6px 8px; }
.map-search { flex: 1; min-width: 0; }
.map-table { width: 100%; border-collapse: collapse; font-size: var(--text-sm); }
.map-table th, .map-table td {
  text-align: left; padding: 10px; border-bottom: 1px solid var(--neutral-200);
  vertical-align: top;
}
.map-table__muted, .map-table__suggest { color: var(--neutral-500); }
.map-actions { display: flex; gap: 6px; flex-wrap: wrap; }
.map-chip {
  border: 1px solid var(--neutral-300); background: var(--neutral-0);
  border-radius: 100px; padding: 3px 10px; font-size: 12px; cursor: pointer;
}
.map-chip--on { background: var(--primary-50); border-color: var(--primary-400); color: var(--primary-700); }
.map-select { border: 1px solid var(--neutral-300); border-radius: 8px; padding: 6px; width: 100%; }
.map-badge { font-size: 10px; padding: 1px 6px; border-radius: 100px; }
.map-badge--warn { background: var(--amber-100, #fef3c7); color: var(--amber-700, #b45309); }
.btn-sm { padding: 4px 12px; font-size: 12px; }
</style>
